import React, { useState, useEffect, useRef } from 'react';
import DateDisplay from '../../atoms/date-display/date-display';
import DatePicker from '../../atoms/DatePicker/DatePickerAtom';
import './DateSelector.scss';

interface DateSelectorProps {
  initialDate: string | null;
  onDateChange: (date: Date | null) => void;
  className?: string;
  dateFormat?: string;
  isFriendly: boolean;
  isReadOnly: boolean;
  permissionCheck?: () => boolean | Promise<boolean>;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  initialDate,
  onDateChange,
  dateFormat,
  isFriendly = false,
  isReadOnly = false,
  className = '',
  permissionCheck,
}) => {
  const [date, setDate] = useState<Date | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // New ref for DatePicker

  useEffect(() => {
    if (initialDate) {
      const parsedDate = new Date(initialDate);
      setDate(isNaN(parsedDate.getTime()) ? null : parsedDate);
    } else {
      setDate(null);
    }
  }, [initialDate]);

  const handleDateChangeInternal = (newDate: Date | null) => {
    setDate(newDate);
    onDateChange(newDate);
    exitEditMode();
  };

  const exitEditMode = () => {
    setTimeout(() => {
      setIsEditing(false);
    }, 100);
  };

  // Updated click outside logic to account for DatePicker's container
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
  
      // If the click happened inside the Primereact Calendar overlay, don't close the picker.
      if (target.closest('.p-datepicker')) {
        return;
      }
  
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !datePickerRef.current?.contains(target)
      ) {
        exitEditMode();
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleDoubleClick = async () => {
    if (isReadOnly) {
      return;
    }
    if (permissionCheck) {
      let hasPermission = false;
      try {
        hasPermission = await permissionCheck();
      } catch (error) {
        hasPermission = false;
      }
      if (!hasPermission) {
        return;
      }
    }
    setIsEditing(true);
  };

  return (
    <div
      ref={containerRef}
      className={`data-molecule ${className}`}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={handleDoubleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleDoubleClick();
        }
      }}
    >
      {isEditing && !isReadOnly ? (
        // Ensure DatePicker forwards the ref to its root element
        <DatePicker
          ref={datePickerRef}
          value={date}
          onDateChange={handleDateChangeInternal}
          dateFormat={dateFormat}
          selectionMode="single"
        />
      ) : (
        // `dateFormat` here is a PrimeReact Calendar pattern (e.g. "dd/mm/yy") for
        // DatePicker's `dateFormat`, not a BCP-47 locale tag — DateDisplay's own
        // `dateFormat` prop expects a locale (e.g. "en-US") and falls back to
        // `navigator.language` when omitted, so it must not receive this value.
        <DateDisplay date={date} isFriendly={isFriendly} />
      )}
    </div>
  );
};

export default DateSelector;
